import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AnimatedCircles from './AnimatedCircles';
import Navigation from './Navigation';
import SocialLinks from './SocialLinks';
import ResumeDownload from './ResumeDownload';
import ScrollIndicator from './ScrollIndicator';
import About from '../About/About';
import Skills from '../Skills/Skills';
import Projects from '../Projects/Projects';
import Contact from '../Contact/Contact';
import { device } from '../../styles/responsive';

const PageContainer = styled.div`
  width: 100%;
  background-color: #000;
`;

const HomeContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  text-align: center;
  color: white;
  padding: 0 20px;
  
  @media ${device.md} {
    padding-top: 0;
    height: 90vh;
  }
  
  @media ${device.sm} {
    height: 85vh;
  }
`;

const Logo = styled(motion.div)`
  position: absolute;
  top: 20px;
  font-size: 32px;
  font-weight: bold;
  letter-spacing: 2px;
  z-index: 5;
  width: 100%;
  text-align: center;
  color: white;
  
  @media ${device.md} {
    text-align: left;
    padding-left: 20px;
    width: auto;
    left: 0;
  }
  
  @media ${device.sm} {
    text-align: left;
    padding-left: 15px;
    width: auto;
    left: 0;
    font-size: 28px;
  }
`;

const TitleContainer = styled(motion.div)`
  z-index: 1;
  margin-bottom: 20px;
  position: relative;
  width: 90%;
  max-width: 1200px;
  
  @media ${device.sm} {
    width: 95%;
    margin-bottom: 5px;
  }
`;

const TitleLine = styled(motion.h1)`
  font-size: 210px;
  font-weight: 800;
  margin: 0;
  margin-bottom: 30px;
  line-height: 0.9;
  position: relative;
  
  @media ${device.xl} {
    font-size: 180px;
  }
  
  @media ${device.lg} {
    font-size: 150px;
  }
  
  @media ${device.md} {
    font-size: 100px;
    margin-bottom: 20px;
  }
  
  @media ${device.sm} {
    font-size: 60px;
    margin-bottom: 10px;
    line-height: 1;
  }
  
  @media ${device.xs} {
    font-size: 48px;
    margin-bottom: 8px;
  }
`;

const TitleFirstLine = styled(TitleLine)`
  position: relative;
`;

const TitleSecondLine = styled(TitleLine)`
  position: relative;
`;

const Subtitle = styled(motion.h2)`
  font-size: 2rem;
  margin-top: 30px;
  opacity: 0.8;
  font-weight: 400;
  
  @media ${device.md} {
    font-size: 1.75rem;
    margin-top: 15px;
  }
  
  @media ${device.sm} {
    font-size: 1.4rem;
    margin-top: 10px;
  }
  
  @media ${device.xs} {
    font-size: 1.2rem;
    margin-top: 5px;
  }
`;

const HomePage = () => {
  const { t } = useTranslation();

  const titleParts = t('Software Engineer').split(' ');
  const firstWord = titleParts[0];
  const secondWord = titleParts.slice(1).join(' ');

  return (
    <PageContainer>
      <HomeContainer id="home">
        <AnimatedCircles />
        
        <Logo
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          MQZ
        </Logo>

        <TitleContainer>
          <TitleFirstLine
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            {firstWord}
          </TitleFirstLine>
          <TitleSecondLine
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
          >
            {secondWord}
          </TitleSecondLine>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: "easeOut" }}
          >
            {t('AI/ML Focus')}
          </Subtitle>
        </TitleContainer>

        <Navigation />
        <SocialLinks />
        <ResumeDownload />
        <ScrollIndicator />
      </HomeContainer>
      <About />
      <Skills />
      <Projects />
      <Contact />
    </PageContainer>
  );
};

export default HomePage;